import { TabItem } from "../../models/components";
import { AlpineContext, ExcludePatternsManagerState, IgnoredVulnerabilitiesFormsetState, IgnoreSeveritiesManagerState, PolicyWizardFormState } from "./interfaces";

/**
   * Manages the state and interaction of policy tabs.
   *
   * @returns An object with tab management functionality
   * @remarks
   * Provides a reactive tab system with three predefined tabs: Production, CI/CD, and Development.
   * Allows switching between tabs by updating their active state.
   *
   * @example
   * const tabs = policiesTab();
   * tabs.switchTabs('CI/CD'); // Activates the CI/CD tab
   */
  export function policiesTab() {
    return {
      tabItems: [
        { label: "Production", isActive: true },
        { label: "CI/CD", isActive: false },
        { label: "Development", isActive: false },
      ],
      switchTabs(tabItem: string) {
        this.tabItems = this.tabItems.map((item: TabItem) => {
          return { ...item, isActive: item.label === tabItem };
        });
      },
    };
  }

/**
 * Creates a policy wizard state management object for handling multi-step form interactions.
 *
 * @remarks
 * Manages form state, tracks changes, handles form submission, and provides step navigation
 * with change tracking and confirmation.
 *
 * @param currentStep - The initial step of the policy wizard
 * @returns An object with methods and properties for managing the policy wizard form state
 *
 * @alpha
 */
export function policyWizard(currentStep: string): PolicyWizardFormState {
  return {
      currentStep: currentStep,
      formChanged: false,
      saving: false,
      initialFormState: null,

      checkFormChanged(this: PolicyWizardFormState & AlpineContext) {
          const form = this.$refs.wizardForm as HTMLFormElement;
          const currentFormData = new FormData(form);
          const entries: [string, FormDataEntryValue][] = [];
          currentFormData.forEach((value, key) => {
              entries.push([key, value]);
          });
          const normalizedEntries: [string, string][] = [];
          const seenForms = new Set<string>();

          // First pass: count non-empty forms
          (entries as [string, FormDataEntryValue][]).forEach(([name, value]) => {
              if (name.includes('vulnerability_id') && value) {
                  seenForms.add(name.split('-')[1]);
              }
          });

          // Second pass: collect non-management form fields
          (entries as [string, FormDataEntryValue][]).forEach(([name, value]) => {
              // Skip management form fields
              if (
                  name.includes('TOTAL_FORMS') ||
                  name.includes('INITIAL_FORMS') ||
                  name.includes('MIN_NUM_FORMS') ||
                  name.includes('MAX_NUM_FORMS')
              ) {
                  return;
              }

              // Handle DELETE fields
              if (name.includes('-DELETE')) {
                  return;
              }

              // Only include non-empty forms
              if (name.includes('vulnerability_id')) {
                  if (value) {
                      seenForms.add(name.split('-')[1]);
                  }
                  return;
              }

              // Include fields only from forms with non-empty vulnerability_ids
              const formIndex = name.split('-')[1];
              if (formIndex && seenForms.has(formIndex)) {
                  normalizedEntries.push([name, value as string]);
              }
          });

          // Sort entries for consistent comparison
          const currentState = normalizedEntries
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(entry => entry.join('='))
              .join('&');

          if (!this.initialFormState) {
              return false;
          }

          return currentState !== this.initialFormState;
      },

      saveInitialState(this: PolicyWizardFormState & AlpineContext) {
          const form = this.$refs.wizardForm as HTMLFormElement;
          const formData = new FormData(form);
        const entries: [string, FormDataEntryValue][] = [];
        formData.forEach((value, key) => {
            entries.push([key, value]);
        });
        const normalizedEntries = entries
            .filter(([name]) =>
                !name.includes('TOTAL_FORMS') &&
                !name.includes('INITIAL_FORMS') &&
                !name.includes('MIN_NUM_FORMS') &&
                !name.includes('MAX_NUM_FORMS')
            )
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(entry => entry.join('='))
            .join('&');

          this.initialFormState = normalizedEntries;
      },

      async handleSubmit(this: PolicyWizardFormState & AlpineContext, event: SubmitEvent) {
          const submitButton = (event.submitter as HTMLButtonElement) || null;

          if (submitButton && submitButton.name === 'save') {
              event.preventDefault();
              this.saving = true;

              const form = event.target as HTMLFormElement;
              const formData = new FormData(form);
              formData.append('save', 'true');

              try {
                  const response = await fetch(form.action, {
                      method: 'POST',
                      body: formData,
                      headers: {
                          'X-Requested-With': 'XMLHttpRequest'
                      }
                  });

                  const data = await response.json();

                  if (response.ok) {
                      this.$dispatch('notify', {
                          content: data.message || 'Changes saved successfully',
                          type: 'success'
                      });
                      this.saveInitialState();
                      this.formChanged = false;
                  } else {
                      throw new Error(data.message || 'Save failed');
                  }
              } catch (error: any) {
                  this.$dispatch('notify', {
                      content: error.message || 'Failed to save changes',
                      type: 'error'
                  });
              } finally {
                  this.saving = false;
              }
          }
      },

      handleStepChange(this: PolicyWizardFormState & AlpineContext, step: string) {
          if (!this.formChanged || confirm('You have unsaved changes that will be lost. Continue?')) {
              (this.$refs.gotoInput as HTMLInputElement).value = step;
              this.formChanged = false;
              (this.$refs.wizardForm as HTMLFormElement).submit();
          }
      },

      init(this: PolicyWizardFormState & AlpineContext) {
          this.saveInitialState();

          this.$watch('formChanged', (value: boolean) => {
              window.onbeforeunload = value ? () => '' : null;
          });

          (this.$refs.wizardForm as HTMLFormElement).addEventListener('input', () => {
              this.formChanged = this.checkFormChanged();
          });

          this.$el.addEventListener('form-changed', () => {
              this.formChanged = this.checkFormChanged();
          });

          this.$el.addEventListener('formset-changed', () => {
              this.formChanged = this.checkFormChanged();
          });
      }
  };
}

/**
 * Creates a manager for handling exclude patterns in a policy configuration.
 *
 * @param excludePatternsFieldId - The ID of the hidden input field storing exclude patterns
 * @param defaultMaxDepth - The default maximum depth for pattern exclusions, defaults to 4
 * @returns An Alpine.js compatible state object for managing exclude patterns
 *
 * @remarks
 * This function provides a comprehensive interface for managing a set of patterns to exclude from processing.
 * It supports adding, removing, and tracking patterns, with built-in validation and hidden input synchronization.
 *
 * @example
 * ```typescript
 * const manager = excludePatternsManager('exclude-patterns-field');
 * manager.addPattern('*.log');
 * manager.addCommonPattern('node_modules');
 * ```
 */
export function excludePatternsManager(
  excludePatternsFieldId: string,
  defaultMaxDepth = 4
): ExcludePatternsManagerState {

  return {
    selectedPatterns: new Set<string>(),
    newPattern: '',
    depthValue: defaultMaxDepth,
    commonPatterns: [
      { name: '.git', description: 'Git version control files and folders' },
      { name: '*.html', description: 'All HTML files' },
      { name: '*.js', description: 'All JavaScript files' },
      { name: '__pycache__', description: 'Python compiler cache files' },
      { name: 'node_modules', description: 'Node.js package dependencies' },
      { name: '.env', description: 'Environment configuration files (may contain secrets)' }
    ],

    init(this: ExcludePatternsManagerState & AlpineContext) {
      const excludeField = document.getElementById(excludePatternsFieldId) as HTMLInputElement;
      if (excludeField && excludeField.value) {
        try {
          const patterns = JSON.parse(excludeField.value);
          if (Array.isArray(patterns)) {
            patterns.forEach((p: string) => this.selectedPatterns.add(p));
          }
        } catch (e) {
          console.error('Error parsing exclude_patterns:', e);
        }
      }
      this.updateHiddenInput();
    },

    addPattern(this: ExcludePatternsManagerState & AlpineContext) {
      const pattern = this.newPattern.trim();
      if (pattern && !this.selectedPatterns.has(pattern)) {
        this.selectedPatterns.add(pattern);
        this.updateHiddenInput();
        this.newPattern = '';
        this.$dispatch('form-changed', true);
      }
    },

    removePattern(this: ExcludePatternsManagerState & AlpineContext, pattern: string) {
      this.selectedPatterns.delete(pattern);
      this.updateHiddenInput();
      this.$dispatch('form-changed', true);
    },

    addCommonPattern(this: ExcludePatternsManagerState & AlpineContext, pattern: string) {
      if (!this.selectedPatterns.has(pattern)) {
        this.selectedPatterns.add(pattern);
        this.updateHiddenInput();
        this.$dispatch('form-changed', true);
      }
    },

    isPatternSelected(this: ExcludePatternsManagerState & AlpineContext, pattern: string) {
      return this.selectedPatterns.has(pattern);
    },

    getSelectedPatterns(this: ExcludePatternsManagerState & AlpineContext) {
      return Array.from(this.selectedPatterns);
    },

    updateHiddenInput(this: ExcludePatternsManagerState & AlpineContext) {
      const excludeField = document.getElementById(excludePatternsFieldId) as HTMLInputElement;
      if (excludeField) {
        excludeField.value = JSON.stringify(this.getSelectedPatterns());
      }
    },

    validateDepth(this: ExcludePatternsManagerState & AlpineContext, value: string) {
      if (value === '') return value; // Allow empty so user can easily adjust
      const numValue = parseInt(value, 10) || 1;
      return Math.min(Math.max(numValue, 1), 10);
    }
  };
}

/**
 * Creates a manager for handling severity level checkbox selections.
 *
 * @remarks
 * This function provides state management for a group of severity checkboxes,
 * allowing for select all, partial selection, and individual checkbox interactions.
 *
 * @returns An object with methods and properties for managing severity checkbox selections
 *
 * @property selectAll - Indicates whether all checkboxes should be selected
 * @property isIndeterminate - Represents a partial selection state
 * @property checkboxes - Collection of checkbox input elements
 * @property allSelected - Indicates if all checkboxes are currently selected
 *
 * @method init - Initializes the severity checkbox manager by setting up references and event listeners
 * @method toggleSelectAll - Toggles the selection state of all checkboxes
 * @method updateSelectAllState - Updates the select all and indeterminate states based on current checkbox selections
 * @method checkAllSelected - Checks if all checkboxes are currently selected
 */
export function ignoreSeveritiesManager(): IgnoreSeveritiesManagerState {
  return {
    selectAll: false,
    isIndeterminate: false,
    checkboxes: [],
    allSelected: false,

    init(this: IgnoreSeveritiesManagerState & AlpineContext) {
      // Wait until the DOM elements are fully rendered
      this.$nextTick(() => {
        const severityOptions = this.$refs.severityOptions as HTMLElement;
        if (!severityOptions) return;

        this.checkboxes = Array.from(severityOptions.querySelectorAll('input[type=checkbox]')) as HTMLInputElement[];

        // Set initial state based on pre-checked boxes
        this.updateSelectAllState();

        // Add event listeners to individual checkboxes
        this.checkboxes.forEach(checkbox => {
          checkbox.addEventListener('change', () => {
            this.updateSelectAllState();
            this.checkAllSelected();
          });
        });

        // Initial check for all selected
        this.checkAllSelected();
      });
    },

    toggleSelectAll(this: IgnoreSeveritiesManagerState & AlpineContext) {
      this.checkboxes.forEach(checkbox => {
        checkbox.checked = this.selectAll;
      });
      this.isIndeterminate = false;
      this.checkAllSelected();
    },

    updateSelectAllState(this: IgnoreSeveritiesManagerState & AlpineContext) {
      const total = this.checkboxes.length;
      const checked = this.checkboxes.filter(cb => cb.checked).length;

      if (checked === total) {
        this.selectAll = true;
        this.isIndeterminate = false;
      } else if (checked === 0) {
        this.selectAll = false;
        this.isIndeterminate = false;
      } else {
        this.selectAll = false;
        this.isIndeterminate = true;
      }
    },

    checkAllSelected(this: IgnoreSeveritiesManagerState & AlpineContext) {
      this.allSelected = this.checkboxes.every(cb => cb.checked);
    }
  };
}


/**
 * Manages a formset for ignored vulnerabilities with dynamic form manipulation.
 *
 * @remarks
 * This function provides state management and methods for adding, deleting, and tracking forms
 * related to ignored vulnerabilities in a Django-style formset.
 *
 * @returns An object with methods and properties for managing the ignored vulnerabilities formset
 *
 * @property totalForms - Total number of forms in the formset
 * @property initialForms - Initial number of forms when the page loads
 *
 * @method init - Initializes the formset by reading total and initial form counts from hidden inputs
 * @method addForm - Dynamically adds a new form to the formset with updated indices
 * @method deleteForm - Marks a specific form for deletion and hides it from the UI
 */
export function ignoredVulnerabilitiesFormsetManager(): IgnoredVulnerabilitiesFormsetState {
  return {
    totalForms: 0,
    initialForms: 0,

    init(this: IgnoredVulnerabilitiesFormsetState & AlpineContext) {
      this.$nextTick(() => {
        const totalFormsInput = document.querySelector<HTMLInputElement>(`[name="ignored_vulnerabilities-TOTAL_FORMS"]`);
        const initialFormsInput = document.querySelector<HTMLInputElement>(`[name="ignored_vulnerabilities-INITIAL_FORMS"]`);

        if (totalFormsInput) {
          this.totalForms = parseInt(totalFormsInput.value, 10);
        }
        if (initialFormsInput) {
          this.initialForms = parseInt(initialFormsInput.value, 10);
        }

        this.$watch('totalForms', value => {
          const totalFormsInput = document.querySelector(`[name="ignored_vulnerabilities-TOTAL_FORMS"]`) as HTMLInputElement;
          if (totalFormsInput) {
            totalFormsInput.value = String(value);
          }
        });
      });
    },

    addForm(this: IgnoredVulnerabilitiesFormsetState & AlpineContext) {
      const template = document.querySelector<HTMLTemplateElement>('#empty-form-template');
      if (!template) return;

      const newForm = template.content.cloneNode(true) as DocumentFragment;
      const newIndex = this.totalForms;

      // Update IDs and names in the new form
      newForm.querySelectorAll<HTMLElement>('[name]').forEach(el => {
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          el.name = el.name.replace(`__prefix__`, String(newIndex));
          if (el.id) {
            el.id = el.id.replace(`__prefix__`, String(newIndex));
          }
        }
      });

      // Update the root div ID
      const rootDiv = newForm.querySelector('div');
      if (rootDiv) {
        rootDiv.id = `form-${newIndex}`;
      }

      // Set up the remove button
      const removeButton = newForm.querySelector<HTMLElement>('[data-delete-form]');
      if (removeButton) {
        removeButton.setAttribute('x-on:click', `deleteForm(${newIndex})`);
      }

      document.querySelector('#formset-container')?.appendChild(newForm);
      this.totalForms++;
      this.$dispatch('form-changed');
    },

    deleteForm(this: IgnoredVulnerabilitiesFormsetState & AlpineContext, index: number) {
      const deleteInput = document.querySelector<HTMLInputElement>(`[name="ignored_vulnerabilities-${index}-DELETE"]`);
      if (deleteInput) {
        deleteInput.value = 'on';
      }

      const formElement = document.querySelector(`#form-${index}`);
      if (formElement) {
        (formElement as HTMLElement).style.display = 'none';
      }

      this.$dispatch('form-changed');
    }
  };
}

interface ClonePolicyComponentState {
  isLoading: boolean;
  submitForm(this: AlpineContext & ClonePolicyComponentState): Promise<void>;
}

export function clonePolicyComponent(): ClonePolicyComponentState {
  return {
    isLoading: false,
    async submitForm(this: AlpineContext & ClonePolicyComponentState): Promise<void> {
      this.isLoading = true;
      const formData = new FormData(document.querySelector('form')!);
      const url = "/organization/policies/clone-default/";

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRFToken': formData.get('csrfmiddlewaretoken') as string,
          }
        });
        const data = await response.json();

        if (data.status === 'success') {
          // We expect reload to happen, but don't call it directly in tests
          if (typeof location !== 'undefined' && location.reload) {
            location.reload();
          }
        } else {
          this.$dispatch('notify', { content: `Error: ${data.message}`, type: 'error' });
        }
      } catch (error) {
        console.error('Policy clone error:', error);
        this.$dispatch('notify', {
          content: `Failed to clone policy. Please try again or contact support if the issue persists.`,
          type: 'error'
        });
      } finally {
        this.isLoading = false; // Reset loading state regardless of outcome
      }
    }
  };
}
